import React, { useState } from 'react';
import Seo from '../../components/Seo';
import NewNavbar from '../../components/NewNavbar';
import NewFooter from '../../components/NewFooter';
import TestimonialsSection from '../../components/HomePage/TestimonialsSection';
import TypedHeading from '../../components/TypedHeading';
import FaqAccordion from '../../components/FaqAccordion';
import PriceAccordion from '../../components/PriceAccordion';
import ContactForm, { CONTACT_FORM_CLASSES } from '../../components/ContactForm/ContactForm';
import './bloodtesting.css';

/* ── Images ─────────────────────────────────────────────────── */
const HERO_BG = '/assets/new_assets/t-ban-pexels-karolina-4047146-scaled.jpg';
const CTA_BG = '/assets/new_assets/video-section-BL.jpg';
const CTA_VIDEO = '/assets/new_assets/blood-testing-online-video-cutter.com-1.mp4';
const STATS_BG = '/assets/new_assets/brooke-lark-08bOYnH_r_E-unsplash_2-scaled.jpg';
const HOW_IMG = '/assets/new_assets/bld-steps.png';
const QUOTE_IMG = '/assets/images/testimonial-quote.png';
const PDF_URL = '/assets/new_assets/MPC-Blood-Test-Sample-Report-.pdf';

/* ── Data ───────────────────────────────────────────────────── */
const PRICELIST = [
  { service: 'Blood Draw Only (Bring your own kit)', price: '£30' },
  { service: 'Allergy Complete Test - 295 Allergens', price: '£299' },
  { service: 'Pregnancy Test - Beta-HCG (Quantitative)', price: '£69' },
  { service: 'Sexual Health - Chlamydia & Gonorrhoea', price: '£75' },
  { service: 'Well Man', price: '£139' },
  { service: 'Well Women', price: '£139' },
];

const TYPED_STRINGS = ['A-Z Blood Test', 'Build Your Package'];

const SEARCH_CARDS = [
  {
    btnText: 'A To Z Blood Test',
    btnHref: '/a-z-tests',
    desc: 'Browse our A-Z list of blood tests to quickly add tests to your selection, review the biomarkers, or view more details for each test.',
  },
  {
    btnText: 'Build Your Own Package',
    btnHref: '/build-my-package',
    desc: 'Add multiple tests to create your personalised package. Our system will generate the most cost-effective and efficient combination based on your requirements.',
  },
];

const STATS = [
  { num: '100 %', label: 'personalised reports', desc: 'Tailored results for your unique health profile' },
  { num: '1 Single Test', label: 'for vital insights', desc: 'Our Comprehensive wellman or wellwomen profile for Total Well-being' },
  { num: '200 +', label: 'health conditions', desc: 'Blood tests can screen for hundreds of conditions' },
  { num: '10 years', label: 'before symptoms', desc: 'Identify conditions a decade before symptoms show' },
];

const HOW_STEPS = [
  {
    num: '01', title: 'Choose Your Test',
    text: "Select your blood test using our 'A-Z page' or 'Build Your Package'. Choose your appointment time and pay online. Rest assured, we offer free rescheduling and refunds should you ever need.",
  },
  {
    num: '02', title: 'Visit Our Clinic',
    text: "Our friendly staff will welcome you and make the blood collection process as stress-free and comfortable as possible. If you need any extra reassurance, we're here to provide any additional support and make the experience more bespoke for you.",
  },
  {
    num: '03', title: 'Receive Your Results',
    text: "Your results will be sent directly via email around the specified time. They include a personalised doctor's report with an easy-to-understand breakdown — all free of charge.",
  },
];

const FAQS = [
  {
    q: 'What Is The Difference In A Biomarker, Profile And Package?',
    a: (
      <ul className="bt-faq__answer-list">
        <li><span className="bt-faq__answer-label">Biomarker:</span> An individual measurable blood test. Most biomarkers can be tested individually. e.g. calcium</li>
        <li><span className="bt-faq__answer-label">Profile:</span> A set of related biomarkers tested together for a comprehensive view of a specific health area. e.g. wellman profile</li>
        <li><span className="bt-faq__answer-label">Package:</span> A bundled collection of profiles and/or biomarkers, tailor-made for you. e.g. calcium + wellman profile</li>
      </ul>
    ),
  },
  {
    q: 'What Is The Difference In Searching By "A-Z Tests" And "Build Your Package"?',
    a: "Searching by ‘A-Z Tests’ lets you browse through our full list of individual tests for easy selection. ‘Build Your Package’ allows you to combine multiple tests, and our system will create the most cost-effective and efficient combination tailored to your needs.",
  },
  {
    q: 'How Much Does It Cost?',
    a: 'The prices vary for different blood tests, depending on how many biomarkers are analysed. All prices are displayed next to the test. These are excluding the £30 phlebotomy fee to withdraw blood.',
  },
  {
    q: 'Do I Need To Fast For My Blood Test?',
    a: (
      <>Most of the blood tests we offer do not require fasting, unless specifically advised by your doctor or if the test is related to fasting (e.g. fasting glucose).<br />However, we recommend drinking plenty of water before your appointment to make the blood collection process easier.</>
    ),
  },
  {
    q: 'What Is The Minimum Age To Have A Blood Test At Your Clinic?',
    a: 'We currently only undertake blood tests for people over 16 years of age.',
  },
  {
    q: 'What Happens During The Appointment And How Long Will It Take?',
    a: 'During your appointment, our friendly staff will take a blood sample from you. The process is quick, typically taking just a few minutes. We aim to make the experience as comfortable and stress-free as possible. Depending on the number of tests, the entire appointment usually takes less than 15 minutes.',
  },
  {
    q: 'What Do I Do If I Am Scared Of Needles?',
    a: 'Our trained staff are here to ensure you feel as comfortable as possible during your blood test. We recommend lying back in our phlebotomy chair to help you relax while the sample is taken. The process only takes a few minutes. If you have any concerns or need extra support, please don’t hesitate to contact us.',
  },
  {
    q: 'Are There Any Side Effects After The Blood Test?',
    a: 'A very small amount of blood is taken, therefore there should not be any severe side effects. A few mild side effects that may occur include soreness and bruising at the site where the needle was inserted. However, this will subside over a few days.',
  },
  {
    q: 'How, Where, And When Do I Get My Results?',
    a: (
      <>We aim to send your results within 3 days, <b>unless otherwise stated.</b> Results will be emailed to you as a PDF file, which includes a personalised doctor’s report and a clear breakdown of your results. You can access them directly in your inbox, making it easy to review and understand your findings. If you have any questions, feel free to contact us.
      </>
    ),
  },
  {
    q: 'Will You Share The Results With My GP?',
    a: 'No, we do not share your results with anybody. However, we suggest discussing any abnormal results with your GP for further investigation.',
  },
  {
    q: 'Can I See A Sample Report Of What My PDF Results May Look Like?',
    a: (
      <>
        Yes – please{' '}
        <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="bt-faq__link">
          click here
        </a><br />
        If you have any questions, please email us.
      </>
    ),
  },
];

const TESTIMONIALS = [
  {
    quote: "I had a fantastic experience with My Private Chemist! I booked a same-day online appointment, and upon arrival, I was welcomed ahead of schedule. The staff was exceptionally fast, friendly, and professional. My pregnancy blood test was done painlessly, and I received my results in less than 5 hours via email. Highly recommend their efficient and reliable services!",
    author: 'Dominika Motylewska',
  },
  {
    quote: "Brilliant service, the pharmacist taking my blood sample was super friendly and professional. Really fast no issues.",
    author: 'Joss Duggan',
  },
  {
    quote: "The best analysis place in central London. Everything is very fast and efficient. The nurse doing blood tests is very friendly, professional and my blood tests are always pain free thanks to her!",
    author: 'Anna Perevalova',
  },
  {
    quote: "Quick, efficient and painless. I'll start off by saying I am terrible with needles and blood, and often get lightheaded at even a mention of them. I did my research and came across this clinic and I am so happy I did.",
    author: 'Jasmine Donaldson',
  },
];

/* ── BloodTestingPage ───────────────────────────────────────── */
const BloodTestingPage = () => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <>
      <Seo
        title="My Private Chemist | Blood Testing"
        description="Fast, accurate private blood testing in London. Choose from our A-Z test list or build your own package, with results and a doctor's report by email."
        canonical={`${process.env.REACT_APP_BASE_URL}/blood-testing`}
        url={`${process.env.REACT_APP_BASE_URL}/blood-testing`}
        image={`${process.env.REACT_APP_BASE_URL}${HERO_BG}`}
      />
      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="bt-hero" style={{ backgroundImage: `url(${HERO_BG})` }}>
        <div className="bt-hero__navbar-wrap">
          <div className="container">
            <div className="row">
              <div className="col-12">
                <NewNavbar />
              </div>
            </div>
          </div>
        </div>
        <div className="bt-hero__content">
          <div className="bt-hero__text-col">
            <h1 className="bt-hero__heading">Blood Testing</h1>
            <nav className="bt-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/" className="bt-hero__bc-link">Home</a>
              <span className="bt-hero__bc-sep">›</span>
              <span className="bt-hero__bc-current">Blood Testing</span>
            </nav>
          </div>
        </div>
      </section>

      <main>
        {/* ── Intro ────────────────────────────────────────────── */}
        <section className="bt-intro">
          <div className="bt-intro__container">
            <h4 className="bt-intro__heading">Know Your Health With A Simple Blood Test</h4>
            <p className="bt-intro__text">
              Our advanced blood testing services provide accurate insights for early detection and personalised care.
              From routine screenings to tests for monitoring chronic conditions, we deliver precise and reliable results.
            </p>
            <p className="bt-intro__text">
              Browse by <b>'A-Z specific blood test'</b> or click <b>'Build Your Own Package'</b> below.
            </p>
          </div>
        </section>

        {/* ── CTA Banner ───────────────────────────────────────── */}
        <section className="bt-cta">
          <video
            className="bt-cta__video"
            src={CTA_VIDEO}
            autoPlay
            muted
            loop
            playsInline
            poster={CTA_BG}
          />
          <div className="bt-cta__container">
            <h2 className="bt-cta__heading">
              We handle your health with care.<br />
              Accurate blood tests, tailored for you.
            </h2>
            <a
              href="#bt-contact"
              className="bt-cta__btn"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('bt-contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Book Appointment
            </a>
          </div>
        </section>

        {/* ── Common Blood Tests Pricelist ─────────────────────── */}
        <PriceAccordion
          sectionClassName="bt-pricelist"
          containerClassName="bt-pricelist__container"
          wrapperClassName="bt-pricelist__accordion"
          barClassName="bt-pricelist__bar"
          labelClosed="Common Blood Tests"
          iconClassName="bt-pricelist__arrow"
          iconOpenClass="open"
          iconContent={'▼'}
          bodyClassName="bt-pricelist__body"
          tableClassName="bt-pricelist__table"
          columns={['Services', 'Price']}
          rows={PRICELIST.map(item => [item.service, item.price])}
        />

        {/* ── Search ───────────────────────────────────────────── */}
        <section className="bt-search">
          <div className="bt-search__container">
            <h1 className="bt-search__heading">
              Search By <TypedHeading strings={TYPED_STRINGS} className="bt-search__heading-typed" />
            </h1>
            <p className="bt-search__overview">
              Discover which blood test you need with ease. Explore our extensive{' '}
              <strong>'A-Z Tests'</strong> of blood tests or{' '}
              <strong>'Build Your Own Package'</strong>{' '}
              fully bespoke by selecting from a diverse range of biomarkers and profiles.
            </p>
            <div className="bt-search__cards">
              {SEARCH_CARDS.map((c, i) => (
                <div className="bt-search__card" key={i}>
                  <div className="bt-search__card-overlay" />
                  <div className="bt-search__card-inner">
                    <a href={c.btnHref} className="bt-search__card-btn">{c.btnText}</a>
                    <p className="bt-search__card-text">{c.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Stats ────────────────────────────────────────────── */}
        <section className="bt-stats" style={{ backgroundImage: `url(${STATS_BG})` }}>
          <div className="bt-stats__container">
            <div className="bt-stats__content">
              <h1 className="bt-stats__heading">
                <span className="bt-stats__heading-accent">Blood Testing</span><br />made easy
              </h1>
              <div className="bt-stats__grid">
                {STATS.map((s, i) => (
                  <div className="bt-stats__item" key={i}>
                    <div className="bt-stats__num">{s.num}</div>
                    <div className="bt-stats__label">{s.label}</div>
                    <p className="bt-stats__desc">{s.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Read Result ──────────────────────────────────────── */}
        <section className="bt-read-result">
          <div className="bt-read-result__container">
            <h4 className="bt-read-result__heading">How Do I Read My Result</h4>
            <p className="bt-read-result__text">
              Results are emailed as a PDF directly to you, including a personalised doctor's report and a breakdown
              of your results from our laboratory, making it easy to read and understand.
            </p>
            <p className="bt-read-result__text">
              View an example report below to get an overview of how your test results will be presented.
            </p> <a href={PDF_URL} className="bt-read-result__btn" target="_blank" rel="noopener noreferrer">
              <svg className="bt-read-result__btn-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
                <polyline points="7,10 12,15 17,10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              Download Example Result
            </a>

          </div>
        </section>

        {/* ── How It Works ─────────────────────────────────────── */}
        <section className="bt-how">
          <div className="bt-how__left" style={{ backgroundImage: `url(${HOW_IMG})` }} />
          <div className="bt-how__right">
            <div className="bt-how__content_right">
              <h1 className="bt-how__heading">How it works</h1>
              <div className="bt-how__steps-nav">
                {HOW_STEPS.map((s, i) => (
                  <React.Fragment key={i}>
                    <button
                      className={`bt-how__step-btn${activeStep === i ? ' active' : ''}`}
                      onClick={() => setActiveStep(i)}
                    >
                      <span className="bt-how__step-num">{s.num}</span>
                      <br />
                      <span className="bt-how__step-title">{s.title}</span>
                    </button>
                    <p className={`bt-how__step-inline${activeStep === i ? ' active' : ''}`}>{s.text}</p>
                    {i < HOW_STEPS.length - 1 && <div className="bt-how__step-sep" />}
                  </React.Fragment>
                ))}
              </div>
              <p className="bt-how__step-text">{HOW_STEPS[activeStep].text}</p>
              {activeStep === 2 && (
                <a href={PDF_URL} className="bt-how__report-link" target="_blank" rel="noopener noreferrer">
                  View Example Report
                </a>
              )}
            </div>
          </div>
        </section>

        {/* ── Contact ──────────────────────────────────────────── */}
        <section className="bt-contact" id="bt-contact">
          <div className="bt-contact__container">
            <h4 className="bt-contact__heading">Still Unsure About Which Test To Choose?</h4>
            <p className="bt-contact__sub">Contact our healthcare team for help in selecting the right test for you.</p>
            <p className="bt-contact__phone">
              <svg className="bt-contact__phone-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 11.5a19.79 19.79 0 01-3.07-8.67A2 2 0 012 .82h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
              </svg>
              <a href="tel:02039879202" className="bt-contact__phone-link">0203 987 9202</a>
            </p>
            <ContactForm
              idPrefix="bt-contact"
              classNames={CONTACT_FORM_CLASSES.bloodTesting}
              submitLabel="Send Message"
              sendingLabel="Sending…"
            />
          </div>
        </section>

        {/* ── FAQ ──────────────────────────────────────────────── */}
        <FaqAccordion
          items={FAQS}
          classNames={{
            section: 'bt-faq',
            container: 'bt-faq__container',
            heading: 'bt-faq__heading',
            list: 'bt-faq__list',
            item: 'bt-faq__item',
            itemOpen: 'open',
            question: 'bt-faq__question',
            icon: 'bt-faq__icon',
            answer: 'bt-faq__answer',
          }}
        />

        {/* ── Testimonials ─────────────────────────────────────── */}
        <div className="testimonial_sec_dyn">
          <TestimonialsSection testimonials={TESTIMONIALS} />
        </div>
      </main>

      <NewFooter />
    </>
  );
};

export default BloodTestingPage;
